/**
 * Trending Content / Products store date window + min clicks in
 * `template_components.details` as: `clickNumber,startDate,endDate,activeTab`
 * (same format as Stay Up To Date / Recent Content create forms).
 *
 * activeTab: 7 | 30 | 90 | 2 (all time) | 1 (custom range).
 * Legacy `14` is treated as Custom when reading.
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getTemplateContentPostIds } from "../../../Utils/SupabaseServices/TemplateAnalyticsDatabase";

dayjs.extend(utc);
dayjs.extend(timezone);

export const TRENDING_DATE_RANGE_LABELS = [
  "Last 7 days",
  "Last 30 days",
  "Last 90 days",
  "All time",
  "Custom",
] as const;

export type TrendingDateRangeLabel = (typeof TRENDING_DATE_RANGE_LABELS)[number];

const LABEL_TO_DAYS: Record<
  Exclude<TrendingDateRangeLabel, "Custom">,
  number | "all"
> = {
  "Last 7 days": 7,
  "Last 30 days": 30,
  "Last 90 days": 90,
  "All time": "all",
};

function userTz() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Default Custom range start — Jan 1 of the current calendar year (user TZ). */
export function trendingCustomDefaultStartDate(): string {
  return dayjs().tz(userTz()).startOf("year").format("YYYY-MM-DD");
}

export function trendingWindowFromLabel(
  label?: string,
): number | "all" | "custom" {
  if (label === "Custom") return "custom";
  if (label && label in LABEL_TO_DAYS) {
    return LABEL_TO_DAYS[label as Exclude<TrendingDateRangeLabel, "Custom">];
  }
  return 7;
}

export function trendingLabelFromDetails(
  details?: string | null,
): TrendingDateRangeLabel {
  const parsed = parseTrendingDetails(details);
  if (parsed.activeTab === 2) return "All time";
  if (parsed.activeTab === 1 || parsed.activeTab === 14) return "Custom";
  if (parsed.activeTab === 30) return "Last 30 days";
  if (parsed.activeTab === 90) return "Last 90 days";
  if (parsed.activeTab === 7) return "Last 7 days";
  if (parsed.startDate && parsed.endDate) {
    const days = dayjs(parsed.endDate).diff(dayjs(parsed.startDate), "day");
    if (days >= 80) return "Last 90 days";
    if (days >= 25) return "Last 30 days";
    if (days >= 5) return "Last 7 days";
  }
  return "Last 7 days";
}

/** Inclusive end date for UI (stored end is exclusive +1 day for presets/custom). */
export function trendingDisplayEndDate(endDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return endDate;
  return dayjs(endDate).subtract(1, "day").format("YYYY-MM-DD");
}

/**
 * Trending details store an exclusive end (+1 day) for the link-in-bio click RPC.
 * Website shop-click RPCs use an inclusive range, same as Website Analytics.
 */
export function trendingWindowToInclusiveShopRange(
  startDate: string,
  endDate: string,
): { startDate: string; endDate: string } {
  const start = String(startDate || "").trim();
  const inclusiveEnd = trendingDisplayEndDate(String(endDate || "").trim());
  if (!start || !inclusiveEnd || inclusiveEnd < start) {
    return { startDate: start, endDate: start || inclusiveEnd };
  }
  return { startDate: start, endDate: inclusiveEnd };
}

export function buildTrendingDetails(
  windowLabelOrDays: string | number | "all" | "custom" = "Last 90 days",
  clickNumber = 1,
  customRange?: { startDate: string; endDate: string },
): string {
  const clicks = Math.max(1, Number(clickNumber) || 1);
  const tz = userTz();

  const isCustom =
    windowLabelOrDays === "Custom" ||
    windowLabelOrDays === "custom" ||
    windowLabelOrDays === 1;

  if (isCustom) {
    const startRaw = String(customRange?.startDate || "").trim();
    const endRaw = String(customRange?.endDate || "").trim();
    const startDate = /^\d{4}-\d{2}-\d{2}$/.test(startRaw)
      ? startRaw
      : trendingCustomDefaultStartDate();
    // Store exclusive end (+1 day) like Stay Up To Date custom save.
    // Caller passes inclusive calendar end from the date inputs.
    let endDate: string;
    if (/^\d{4}-\d{2}-\d{2}$/.test(endRaw)) {
      const start = dayjs(startDate);
      const endInclusive = dayjs(endRaw);
      // Clamp inverted ranges to a 1-day window so analytics isn't queried
      // with end <= start (half-open → always empty → "No data").
      const inclusive = endInclusive.isBefore(start, "day")
        ? start
        : endInclusive;
      endDate = inclusive.add(1, "day").format("YYYY-MM-DD");
    } else {
      // Match classic create: local calendar +1, no tz→utc day shift.
      endDate = dayjs().add(1, "day").format("YYYY-MM-DD");
    }
    return `${clicks},${startDate},${endDate},1`;
  }

  const windowDays =
    typeof windowLabelOrDays === "number" || windowLabelOrDays === "all"
      ? windowLabelOrDays
      : trendingWindowFromLabel(windowLabelOrDays);

  if (windowDays === "custom") {
    return buildTrendingDetails("Custom", clicks, customRange);
  }

  const endDate = dayjs().tz(tz).utc().add(1, "day").format("YYYY-MM-DD");
  const activeTab = windowDays === "all" ? 2 : windowDays;
  const startDate =
    windowDays === "all"
      ? dayjs().tz(tz).utc().subtract(100000, "day").format("YYYY-MM-DD")
      : dayjs().tz(tz).utc().subtract(windowDays, "day").format("YYYY-MM-DD");
  return `${clicks},${startDate},${endDate},${activeTab}`;
}

export function parseTrendingDetails(details?: string | null): {
  clickNumber: number;
  startDate: string;
  endDate: string;
  activeTab: number;
} {
  const parts = String(details || "")
    .split(",")
    .map((p) => p.trim());
  const clickNumber = Math.max(1, Number(parts[0]) || 1);
  const startDate = /^\d{4}-\d{2}-\d{2}$/.test(parts[1] || "") ? parts[1] : "";
  const endDate = /^\d{4}-\d{2}-\d{2}$/.test(parts[2] || "") ? parts[2] : "";
  const activeTab = Number(parts[3]);
  return {
    clickNumber,
    startDate,
    endDate,
    activeTab: Number.isFinite(activeTab) ? activeTab : 7,
  };
}

/**
 * Preset tabs (7 / 30 / 90 / all) store absolute dates that go stale.
 * Always query "now" for those tabs so Last 90 days includes Last 30 days.
 * Custom (1 / legacy 14) keeps the stored range.
 */
export function resolveTrendingAnalyticsDates(details?: string | null): {
  clickNumber: number;
  startDate: string;
  endDate: string;
  activeTab: number;
} {
  const parsed = parseTrendingDetails(details);
  if (
    parsed.activeTab === 7 ||
    parsed.activeTab === 30 ||
    parsed.activeTab === 90 ||
    parsed.activeTab === 2
  ) {
    return parseTrendingDetails(
      buildTrendingDetails(
        parsed.activeTab === 2 ? "all" : parsed.activeTab,
        parsed.clickNumber,
      ),
    );
  }
  return parsed;
}

export function normalizeTrendingDetails(details?: string | null): string {
  const range = resolveTrendingAnalyticsDates(details);
  if (!range.startDate || !range.endDate) {
    return buildTrendingDetails("Last 90 days", range.clickNumber);
  }
  return `${range.clickNumber},${range.startDate},${range.endDate},${range.activeTab}`;
}

/**
 * Filter identity for live Edit sync. Preset / All-time tabs roll absolute
 * dates on open — ignore that drift so the canvas does not refetch.
 * Custom ranges still compare full start/end.
 */
export function trendingDetailsFilterKey(details?: string | null): string {
  const parsed = parseTrendingDetails(details);
  if (parsed.activeTab === 1 || parsed.activeTab === 14) {
    return `${parsed.clickNumber}|${parsed.startDate}|${parsed.endDate}|${parsed.activeTab}`;
  }
  return `${parsed.clickNumber}|${parsed.activeTab}`;
}

function windowsForTrendingContent(
  current: ReturnType<typeof resolveTrendingAnalyticsDates>,
) {
  const clicks = current.clickNumber;
  const preset = (tab: number) =>
    resolveTrendingAnalyticsDates(
      buildTrendingDetails(tab === 2 ? "all" : tab, clicks),
    );
  const covers = (window: { startDate: string; endDate: string }) =>
    Boolean(current.startDate && current.endDate) &&
    current.startDate <= window.startDate &&
    current.endDate >= window.endDate;
  const smaller =
    current.activeTab === 30
      ? [preset(7)]
      : current.activeTab === 90
        ? [preset(7), preset(30)]
        : current.activeTab === 2
          ? [preset(7), preset(30), preset(90)]
          : current.activeTab === 1 || current.activeTab === 14
            ? [preset(7), preset(30), preset(90)].filter(covers)
            : [];
  return [...smaller, current];
}

async function trendingContentClickIds(args: {
  userId: string;
  username: string;
  startDate: string;
  endDate: string;
  minClicks: number;
}): Promise<string[]> {
  // Website editor / canvas only. Same Post Click source as Content → Top viewed.
  // Link-in-bio trending content does not call this.
  const range = trendingWindowToInclusiveShopRange(args.startDate, args.endDate);
  if (!range.startDate || !range.endDate) return [];
  return getTemplateContentPostIds({
    username: args.username,
    userId: args.userId,
    startDate: range.startDate,
    endDate: range.endDate,
    minClicks: args.minClicks,
    max: 5000,
  });
}

export type TrendingContentEditorIds = {
  ids: string[];
  windows: string[][];
};

/** Trending Content add/edit. `windows` is smaller ranges first, then the selected range. */
export async function loadTrendingContentEditorPostIds(args: {
  userId: string;
  username: string;
  details?: string | null;
}): Promise<TrendingContentEditorIds> {
  const current = resolveTrendingAnalyticsDates(args.details);
  if (!args.userId || !args.username || !current.startDate || !current.endDate) {
    return { ids: [], windows: [] };
  }
  const lists = await Promise.all(
    windowsForTrendingContent(current).map((window) =>
      trendingContentClickIds({
        userId: args.userId,
        username: args.username,
        startDate: window.startDate,
        endDate: window.endDate,
        minClicks: current.clickNumber,
      }),
    ),
  );
  const windows = lists.map((list) => {
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const id of list) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  });
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const list of windows) {
    for (const id of list) {
      if (seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return { ids, windows };
}

export function trendingContentHydrateIds(
  windows: string[][],
  limit: number,
): string[] {
  const cap = Math.max(limit * 2, 25);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const list of windows) {
    let kept = 0;
    for (const id of list) {
      if (!id || seen.has(id)) continue;
      if (kept >= cap) break;
      seen.add(id);
      out.push(id);
      kept += 1;
    }
  }
  return out;
}

export function trendingContentPostsForRange<T>(
  posts: T[],
  windows: string[][],
  limit: number,
  idOf: (post: T) => string,
): T[] {
  if (!windows.length || limit <= 0) return [];
  const cap = Math.max(limit * 2, 25);
  const seenCap = new Set<string>();
  const capped = windows.map((list) => {
    const out: string[] = [];
    let kept = 0;
    for (const id of list) {
      if (!id || seenCap.has(id)) continue;
      if (kept >= cap) break;
      seenCap.add(id);
      out.push(id);
      kept += 1;
    }
    return out;
  });
  const byId = new Map<string, T>();
  const rowOrder: string[] = [];
  for (const post of posts) {
    const id = idOf(post);
    if (!id || byId.has(id)) continue;
    byId.set(id, post);
    rowOrder.push(id);
  }
  let pinned: string[] = [];
  for (let i = 0; i < capped.length; i++) {
    const allowed = new Set<string>();
    for (const list of capped.slice(0, i + 1)) {
      for (const id of list) if (id) allowed.add(id);
    }
    const ranked = rowOrder.filter((id) => allowed.has(id));
    const top = ranked.slice(0, limit);
    const mustIds = pinned.filter((id) => byId.has(id));
    const mustSet = new Set(mustIds);
    const selected = new Set(top);
    for (const id of mustIds) selected.add(id);
    const inOrder = ranked.filter((id) => selected.has(id));
    if (inOrder.length <= limit) {
      pinned = inOrder;
      continue;
    }
    const keptOptional = new Set(
      inOrder
        .filter((id) => !mustSet.has(id))
        .slice(0, Math.max(0, limit - mustIds.length)),
    );
    pinned = inOrder.filter(
      (id) => mustSet.has(id) || keptOptional.has(id),
    );
  }
  return pinned
    .map((id) => byId.get(id))
    .filter((post): post is T => Boolean(post));
}

/** Update only the click threshold; keep the stored date window as-is. */
export function withTrendingClickNumber(
  details: string | null | undefined,
  clickNumber: number,
): string {
  const parsed = parseTrendingDetails(details);
  const clicks = Math.max(1, Math.floor(Number(clickNumber) || 1));
  if (!parsed.startDate || !parsed.endDate) {
    return buildTrendingDetails(trendingLabelFromDetails(details), clicks);
  }
  return `${clicks},${parsed.startDate},${parsed.endDate},${parsed.activeTab}`;
}
